import React from 'react'
import { ChevronDown } from 'lucide-react'
import { Button, Modal } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import type { useTurbineManagementViewModel } from '../useTurbineManagementViewModel'

type VM = ReturnType<typeof useTurbineManagementViewModel>

interface AddTurbineModalProps {
  showAddModal: VM['showAddModal']
  /** Tek kapanis yolu: backdrop / ESC / X / Iptal hepsi buraya duser. */
  onClose: VM['closeAddModal']
  newTurbine: VM['newTurbine']
  setNewTurbine: VM['setNewTurbine']
  validationErrors: VM['validationErrors']
  selectedAddTurbineCompanyId: VM['selectedAddTurbineCompanyId']
  setSelectedAddTurbineCompanyId: VM['setSelectedAddTurbineCompanyId']
  isAddTurbineCompanyDropdownOpen: VM['isAddTurbineCompanyDropdownOpen']
  setIsAddTurbineCompanyDropdownOpen: VM['setIsAddTurbineCompanyDropdownOpen']
  addTurbineCompanyDropdownRef: VM['addTurbineCompanyDropdownRef']
  hasAdminAccess: VM['hasAdminAccess']
  companies: VM['companies']
  handleAddTurbine: VM['handleAddTurbine']
  isSubmitting: VM['isAddingTurbine']
  error: VM['addError']
  onDismissError: VM['dismissAddError']
}

const AddTurbineModal: React.FC<AddTurbineModalProps> = ({
  showAddModal,
  onClose,
  newTurbine,
  setNewTurbine,
  validationErrors,
  selectedAddTurbineCompanyId,
  setSelectedAddTurbineCompanyId,
  isAddTurbineCompanyDropdownOpen,
  setIsAddTurbineCompanyDropdownOpen,
  addTurbineCompanyDropdownRef,
  hasAdminAccess,
  companies,
  handleAddTurbine,
  isSubmitting,
  error,
  onDismissError,
}) => {
  const { t, language } = useLanguage()

  return (
    <Modal
      isOpen={showAddModal}
      onClose={onClose}
      title={t.dashboard.addNewTurbine}
      size="md"
      // Hata modalin ICINDE: sayfa govdesindeki banner backdrop altinda kalir.
      error={error}
      onDismissError={onDismissError}
      isSubmitting={isSubmitting}
      footer={
        <div className="flex space-x-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
          >
            {t.dashboard.cancel}
          </Button>
          <Button
            onClick={handleAddTurbine}
            className="flex-1 bg-green-600 hover:bg-green-700"
            loading={isSubmitting}
            disabled={isSubmitting || !!validationErrors.rooms}
          >
            {t.dashboard.add}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Turbine Name -> neon 'turbine_name' */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t.dashboard.turbineName}</label>
          <input
            type="text"
            value={newTurbine.turbine_name || ''}
            onChange={(e) => setNewTurbine({ ...newTurbine, turbine_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.turbineNamePlaceholder}
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t.dashboard.city}</label>
          <input
            type="text"
            value={newTurbine.city}
            onChange={(e) => setNewTurbine({ ...newTurbine, city: e.target.value.toLowerCase() })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.cityPlaceholder}
          />
        </div>

        {/* Company (Admin only) */}
        {hasAdminAccess && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.dashboard.company}</label>
            <div className="relative" ref={addTurbineCompanyDropdownRef}>
              <button
                type="button"
                onClick={() => setIsAddTurbineCompanyDropdownOpen(!isAddTurbineCompanyDropdownOpen)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-left flex items-center justify-between bg-white"
              >
                <span className={selectedAddTurbineCompanyId === 'all' ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedAddTurbineCompanyId === 'all' 
                    ? t.dashboard.selectCompany 
                    : companies.find(c => c.id === selectedAddTurbineCompanyId)?.name || t.dashboard.selectCompany
                  }
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-700 transition-transform ${isAddTurbineCompanyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isAddTurbineCompanyDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-48 overflow-y-auto">
                  {companies.map(company => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => {
                        setSelectedAddTurbineCompanyId(company.id)
                        setIsAddTurbineCompanyDropdownOpen(false)

                        // "Sirket secin" hatasi giderildi -> hata kutusu kalksin.
                        onDismissError()
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-primary-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                        selectedAddTurbineCompanyId === company.id ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
                      }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t.dashboard.turbineCode}</label>
          <input
            type="text"
            value={newTurbine.code}
            onChange={(e) => setNewTurbine({ ...newTurbine, code: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.codePlaceholder}
          />
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t.dashboard.location}</label>
          <input
            type="text"
            value={newTurbine.location}
            onChange={(e) => setNewTurbine({ ...newTurbine, location: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.locationPlaceholder}
          />
        </div>

        {/* Site Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {language === 'tr' ? 'Saha İsmi' : 'Site Name'}
          </label>
          <input
            type="text"
            value={newTurbine.site_name}
            onChange={(e) => setNewTurbine({ ...newTurbine, site_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={language === 'tr' ? 'Saha ismini girin' : 'Enter site name'}
          />
        </div>

        {/* Coordinates */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {language === 'tr' ? 'Koordinatlar' : 'Coordinates'}
          </label>
          <input
            type="text"
            value={newTurbine.coordinates}
            onChange={(e) => setNewTurbine({ ...newTurbine, coordinates: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={language === 'tr' ? 'Örn: 41.0082,28.9784' : 'e.g: 41.0082,28.9784'}
          />
        </div>
      </div>
    </Modal>
  )
}

export default AddTurbineModal
